// The till's own state: the menu it sells from, and the sale in flight.
//
// Kept out of the screen so the screen stays a screen. The lock overlay must
// never unmount what it covers, which means the panel a cashier had open has
// to survive being covered -- and state that lives in a component does not
// survive a component that unmounts.

import { api, store } from '../api/client';
import type { components } from '../api/types';
import type { components as storeComponents } from '../api/store';
import { BUNDLE_COMMIT, checkBuild, reloadNowIfNewer } from '../build.svelte';
import {
	beginCheckout,
	cartSent,
	clearDraft,
	currentDraft,
	currentPending,
	draftBody,
	endCheckout,
	forgetOpenOrder,
	noteAddition,
	renewKey,
	repriceDraft,
	restoreLine,
	setDiscount,
	setDriver,
	setTable,
	type DraftLine,
} from '../draft/draft.svelte';
import type { OrderDiscount } from '../domain/pricing';
import {
	checkoutFate,
	discountUnknown,
	keyAnswered,
	onTheDiscount,
	problemOf,
	refusesTheDiscount,
	refusesTheMenuVersion,
	refusesTheTable,
	storeNotReady,
	type Problem,
} from './answer';
import { loadTables } from './tables.svelte';
import { askDrivers, loadDrivers } from './drivers.svelte';
import { retryAfterSeconds, type PasscodeResult } from './passcode.svelte';
import type { MessageKey } from '../i18n/messages';
import { setOpenCount } from './openCount.svelte';
import { askWeighed } from './weighed';

export type Menu = components['schemas']['MenuBody'];
export type Area = components['schemas']['AreaBody'];

/** How often a till re-reads the menu its order store keeps. Five seconds,
 * so a menu the store took on its own check (every 150 to 450 seconds, #349)
 * reaches the screen before the next order is rung up. */
export const MENU_POLL_MS = 5000;

let menu = $state<Menu>({ categories: [], items: [], groups: [], discounts: [], webBuild: '' });
let areas = $state<Area[]>([]);
// Whether the till's order store answers (#371).
//
// The store takes every sale and keeps it until the server has it, so a
// restaurant whose internet is down still sells, and the screen says nothing
// about the internet: the browser's own online event would say a sale cannot
// finish when it can. A store that does not answer is the one thing that
// stops a sale here, and a failed call is what says so.
let online = $state(true);

export function currentMenu(): Menu {
	return menu;
}

// The cart whose discount came off because the owner removed it (UC-35), by
// its key: a menu read took it off, the checkout's refusal did, or an Undo
// left it off. The till says so while that cart is the one on screen, until
// the cashier moves on. A cart that ends, by a sale, gets a new key and takes
// the notice with it.
let removedFrom = $state<string | null>(null);

export function discountRemoved(): boolean {
	return removedFrom !== null && removedFrom === currentDraft().idempotencyKey;
}

export function forgetRemoved(): void {
	removedFrom = null;
}

// The cart's discount came off before its first Send. No Payment sheet can be
// open over that cart: every order is sent before it is paid (#348), and a
// sent order keeps the copy of its discount.
function noteRemoved(): void {
	removedFrom = currentDraft().idempotencyKey;
}

// Whether a menu has arrived at least once. Before then an empty menu is not
// "this shop has no menu", it is a menu on its way.
let menuLoaded = $state(false);

export function hasMenu(): boolean {
	return menuLoaded;
}

/**
 * Undo a line's removal (UC-35): the line comes back where it was, and the
 * discount it took as the cart's last line comes back with it. A manager's
 * typed discount always comes back; a list discount only while the owner still
 * offers it. The menu in hand says so, as its next read would:
 * one the owner removed meanwhile stays off, and the till says why, and one
 * the owner edited comes back as the owner has it now. A menu that has not
 * arrived yet, or that carries no list, from a server that predates it, says
 * nothing, and the next read decides.
 */
export function undoLineRemoval(line: DraftLine, at: number, discount: OrderDiscount | null): void {
	if (restoreLine(line, at, discount, menuLoaded ? menu.discounts : undefined)) noteRemoved();
}

export function currentAreas(): Area[] {
	return areas;
}

export function isOnline(): boolean {
	return online;
}

/** Read the menu. A failure keeps the menu already in hand: a till with a
 * slightly old menu still sells, and one with no menu cannot. */
export async function loadMenu(): Promise<void> {
	try {
		// Weighed items whole (#917): a screen that does not ask gets none.
		const { data, error } = await api.GET('/api/till/menu', { querySerializer: askWeighed });
		if (error !== undefined || !data) return;
		menu = data;
		menuLoaded = true;
		// The API runs another build than this bundle, so Pages may serve a
		// newer one (INT-7). The till asks; the screen moves at rest.
		if (data.webBuild !== BUNDLE_COMMIT) void checkBuild();
		// The cart holds snapshot prices so it can price itself between reads.
		// This is where those snapshots catch up, and where a discount the
		// owner removed comes off the cart, which the till then says.
		if (repriceDraft(data)) noteRemoved();
		online = true;
	} catch {
		online = false;
	}
}

export async function loadAreas(): Promise<void> {
	try {
		const { data, error } = await api.GET('/api/till/areas');
		if (error !== undefined || !data) return;
		areas = data.areas ?? [];
	} catch {
		// The delivery panel falls back to the shop's own fee, which is what a
		// shop with no areas charges anyway.
	}
}

let quotedFeeMinor = $state<number | null>(null);

/**
 * The delivery fee the server says this order carries, or null.
 *
 * The cart prices itself for speed, but it cannot price a delivery: the fee
 * comes from an area it was not told about, or from the shop's own default,
 * which the till is never sent. So a delivery asks. A takeaway never does --
 * its fee is zero by rule, and a round trip per tap would be a round trip for
 * nothing.
 */
export function currentQuotedFee(): number | null {
	return quotedFeeMinor;
}

export function forgetQuote(): void {
	quotedFeeMinor = null;
}

/** Ask what this order would cost. Used for the delivery fee only. */
export async function quoteDelivery(): Promise<void> {
	const draft = currentDraft();
	if (draft.type !== 'delivery' || draft.lines.length === 0) {
		quotedFeeMinor = null;
		return;
	}
	// Without the discount, as the plan's UC-35 says. The API's quote takes one
	// (UC-2), but the till asks it only for the fee, which the discount does
	// not change; a discount the server refuses here, one the owner just
	// removed or one an API a revert put back does not know, would hide the fee.
	// It names no menu version either (#343): the fee is not on the menu.
	const body = draftBody();
	delete body.discount;
	try {
		const { data, error } = await api.POST('/api/till/orders/quote', {
			body: body as never,
		});
		online = true;
		// A refusal here is ordinary: a delivery with no phone yet is not a
		// legal order, and the panel is already saying so. The fee stays
		// unknown rather than becoming a wrong number.
		quotedFeeMinor = error === undefined && data ? data.deliveryFeeMinor : null;
	} catch {
		online = false;
		quotedFeeMinor = null;
	}
}

export type Placed = components['schemas']['CheckoutResultBody'];
export type Order = storeComponents['schemas']['OrderBody'];

// The open order on Sell (#348): every order is sent before it is paid, and
// from its first Send the till's order store holds it and its sent lines. The
// draft keeps its id, so a lock, a reload or another cashier's unlock comes
// back to it; this is the order as the store last answered it, which Sell
// shows above the lines not sent yet.
let openOrder = $state<Order | null>(null);

/** The open order on Sell, as the store last answered it, or null. */
export function currentOpenOrder(): Order | null {
	const id = currentDraft().openOrderId;
	return id && openOrder?.id === id ? openOrder : null;
}

/** Sell shows this order as the store answered it. */
export function showOpenOrder(order: Order | null): void {
	openOrder = order;
}

/**
 * Read the open order the draft names from the store's day, which lists this
 * till's open orders of every trading day (#348). One the store no longer
 * holds open, paid or voided meanwhile, leaves the draft: its lines not sent
 * yet stay, as a new cart.
 */
export async function loadOpenOrder(): Promise<void> {
	const id = currentDraft().openOrderId;
	if (!id) {
		openOrder = null;
		return;
	}
	const day = await loadDay();
	// The cart may have moved on while the day was read: New order, or Open
	// on another order. The answer is about the order it named then.
	if (day === null || currentDraft().openOrderId !== id) return;
	const order = (day.orders ?? []).find((o) => o.id === id);
	if (order?.status === 'open') {
		openOrder = order;
		return;
	}
	openOrder = null;
	if (currentDraft().lines.length === 0) clearDraft();
	else forgetOpenOrder();
}

export type CheckoutOutcome =
	| { kind: 'placed'; placed: Placed }
	// A first Send whose key had already placed the order, under another
	// cart (#348): the order is open, and Sell shows it as the store holds it.
	| { kind: 'alreadySent' }
	| { kind: 'priceChanged'; totalMinor: number }
	// The key already placed an order, under a different cart. The sale is not
	// lost and the cashier must not be told it failed: the order exists, and
	// the number is what they need.
	| { kind: 'alreadyPlaced'; number: number }
	// UC-35. The owner removed the discount: it is off the cart now. A
	// typed discount needs a manager's session. An API a revert put back,
	// which predates discounts, refuses the field itself, and once the key
	// says no sale was made the cashier takes the discount off to sell. And
	// the current API may refuse the discount by a rule of its own.
	| { kind: 'discountRemoved' }
	| { kind: 'discountManagerOnly' }
	| { kind: 'discountsDown' }
	| { kind: 'discountRefused' }
	// UC-39. The shop has tables and the dine-in names none: the till asks
	// which. Or the owner removed the table the cart named, or it is not this
	// shop's: it is off the cart now, and the till asks for another.
	| { kind: 'tableNeeded' }
	| { kind: 'tableRemoved' }
	// UC-4.R6. The delivery names no driver: the till asks which. Or the one
	// the cart named was removed, or the store holds no list naming him: he
	// is off the cart now, and the till asks for another.
	| { kind: 'driverNeeded' }
	| { kind: 'driverRemoved' }
	| { kind: 'refused'; code: string }
	// The sale stays unsent: a refusal that can come before the API reads the
	// key says nothing about a send of it whose answer was lost, so the
	// cashier sees "Checkout not confirmed", never that it did not go through.
	| { kind: 'unconfirmed'; code: string }
	// The till's order store is not ready to sell yet (#371): it has had no
	// server answer since it started, or has not read its menu. Nothing was
	// sold, and the cart is kept for a try in a moment.
	| { kind: 'notReady' }
	| { kind: 'unreachable' };

/**
 * Send the sale.
 *
 * The pending record is written before the request and cleared only when the
 * till's order store says what became of the sale, so a till that dies in
 * between still knows a sale may exist. A 2xx with the order clears it, and so
 * does a 4xx refusal in the API's words to a first send: a refusal is an
 * answer, the order it refused to make does not exist, and no earlier send of
 * the key was left unanswered, or its record would be here. So does the
 * store's 503 that it is not ready to sell, to a first send (storeNotReady).
 * Any other 5xx, a 423, a body that is not the API's and no answer at all keep
 * it (src/till/answer.ts). So does
 * any refusal of a sale's discount as a whole, which an API older than
 * discounts gives before it reads the key, until the key has answered
 * (askTheKey), and any refusal of the sale's menu version, which an API older
 * than menu versions gives before it reads the key: the till reads the menu
 * again and sends the record's body, which names no version, at once under
 * the same key (#343). A resend, which follows a send whose answer was lost,
 * is cleared only by an answer about the key itself (keyAnswered, INT-7).
 */
export async function checkout(
	payment: { method: 'cash' | 'card'; tenderedMinor: number } | null,
	expectedTotalMinor: number,
): Promise<CheckoutOutcome> {
	const draft = currentDraft();
	const body = {
		...draftBody(),
		expectedTotalMinor,
		...(draft.startedBy ? { startedBy: draft.startedBy } : {}),
		...(payment ? { payment } : {}),
	};
	// A record already here is a send of this sale that was never answered:
	// a second tap on the Payment sheet after a lost answer is a resend.
	const resend = currentPending() !== null;
	beginCheckout(body, expectedTotalMinor);
	// The first send names the menu version the cart was priced with, which
	// the server takes for 10 minutes after a newer one replaced it (#343).
	// The record keeps the body without it: a resend is answered by its key
	// first, and an API a revert put back, which predates menu versions,
	// could not read the field.
	const named = !resend && menu.menuVersion ? { ...body, menuVersion: menu.menuVersion } : body;
	return send(named, draft.idempotencyKey, resend);
}

/** Send the sale that was never answered, under the key it already used. */
export async function resumeCheckout(): Promise<CheckoutOutcome | null> {
	const waiting = currentPending();
	if (!waiting) return null;
	return send(waiting.body, waiting.idempotencyKey, true);
}

async function send(body: unknown, key: string, resend: boolean): Promise<CheckoutOutcome> {
	try {
		// A screen that reads drivers says so (UC-4.R6), on every send of
		// the sale, a resend included: the store reads the key first.
		const { data, error, response } = await api.POST('/api/till/orders', {
			body: body as never,
			headers: { 'Idempotency-Key': key },
			querySerializer: askDrivers,
		});
		online = true;
		const fate = checkoutFate(response.status, data, error);
		if (fate === 'placed' && data) {
			endCheckout();
			sentOrPaid(data.order);
			return { kind: 'placed', placed: data };
		}
		const failure = problemOf(error);
		if (storeNotReady(response.status, failure)) {
			// The store read nothing, so a resend's earlier send may still
			// hold the sale: its record stays, and Retry asks again.
			if (resend) return { kind: 'unconfirmed', code: failure?.code ?? '' };
			endCheckout();
			return { kind: 'notReady' };
		}
		if (fate === 'unknown') return unanswered(response.status, failure);
		if (namesTheMenu(body) && refusesTheMenuVersion(failure)) {
			// Only an API older than menu versions, which a revert put back,
			// refuses the field, before it reads the key (#343). The till reads
			// the menu again, whose older body names no version, so it stops
			// naming one, and sends the sale the record holds at once, under
			// the same key, which the key answers first.
			await loadMenu();
			return send(withoutMenuVersion(body), key, resend);
		}
		// A resend follows a send whose answer was lost, and the server may
		// have placed that sale. A refusal that can come before the API reads
		// the key, such as a session's that ended in between, says nothing
		// about it (INT-7): only an answer about the key ends the record.
		const unsettled = resend && !keyAnswered(response.status, failure);
		const onDiscount = onTheDiscount(failure);
		if (carriesTable(body) && refusesTheTable(failure)) {
			// An API older than tables, which a revert put back, refuses a
			// body that names a table before it reads the key, so the key
			// answers first, asked without the table (UC-39, INT-7).
			return await askTheKey(body, key, failure, 'tableId');
		}
		if (carriesDiscount(body) && (refusesTheDiscount(failure) || (unsettled && onDiscount))) {
			// An API older than this bundle, which a revert put back, refuses a
			// body that names a discount before it reads the key, in words that
			// are no contract. So any refusal of the discount as a whole is no
			// answer about the sale yet (INT-7): the key answers first. So does
			// a resend's refusal of a part of it, which the schema can give
			// before the key is read, and which the cashier could not change
			// while the record stays.
			return await askTheKey(body, key, failure, 'discount');
		}
		if (unsettled) {
			// The record stays, and Retry sends the same key: once the cashier
			// is signed in again, or the API is back, the key answers.
			return { kind: 'unconfirmed', code: failure?.code ?? String(response.status) };
		}
		endCheckout();
		if (failure?.code === 'IDEMPOTENCY_MISMATCH') return await takenBefore(body, failure);
		const path = failure?.details?.path ?? '';
		if (
			failure?.code === 'DISCOUNT_UNAVAILABLE' ||
			(failure?.code === 'NOT_FOUND' && path === 'discount.discountId')
		) {
			// Off the cart, as the next menu read would take it; the cashier
			// picks another or sells without one.
			setDiscount(null);
			noteRemoved();
			void loadMenu();
			return { kind: 'discountRemoved' };
		}
		if (failure?.code === 'FORBIDDEN' && onDiscount) {
			return { kind: 'discountManagerOnly' };
		}
		if (failure?.code === 'TABLE_REQUIRED') {
			// The owner added the shop's first table since the till read its
			// list: the till reads it again and asks which table.
			void loadTables();
			return { kind: 'tableNeeded' };
		}
		if (
			failure?.code === 'TABLE_UNAVAILABLE' ||
			(failure?.code === 'NOT_FOUND' && path === 'tableId')
		) {
			// Off the cart, as the next read of the list would take it; the
			// cashier picks another.
			setTable(null);
			void loadTables();
			return { kind: 'tableRemoved' };
		}
		if (failure?.code === 'DRIVER_REQUIRED') {
			// The delivery names no driver: the till reads the list again and
			// asks which (T33).
			void loadDrivers();
			return { kind: 'driverNeeded' };
		}
		if (
			failure?.code === 'DRIVER_INACTIVE' ||
			(failure?.code === 'NOT_FOUND' && path === 'driverId')
		) {
			// Off the cart, as the next read of the list would take him; the
			// cashier picks another.
			setDriver(null);
			void loadDrivers();
			return { kind: 'driverRemoved' };
		}
		if (failure?.code === 'VALIDATION_ERROR' && onDiscount) {
			// The current API refused this discount by its own rule, such as a
			// typed amount above the most a price may be. Nothing moved: the
			// cashier changes the discount or takes it off.
			return { kind: 'discountRefused' };
		}
		if (failure?.code === 'TOTAL_MISMATCH') {
			// The menu or a discount moved under the draft. Re-read it so the
			// cart shows the price the next attempt will be charged.
			void loadMenu();
			// Or the rule that prices it moved, in a release this bundle
			// predates: then the newer bundle is what can price it, and the
			// draft survives the reload (INT-7).
			void reloadNowIfNewer();
			return { kind: 'priceChanged', totalMinor: failure.details?.totalMinor ?? 0 };
		}
		return { kind: 'refused', code: failure?.code ?? String(response.status) };
	} catch {
		// No answer at all. The pending record stays, which is the whole point:
		// the next boot finds it and sends the same key again.
		online = false;
		return { kind: 'unreachable' };
	}
}

/** Whether a checkout body names the menu version it was priced with (#343). */
function namesTheMenu(body: unknown): boolean {
	return typeof body === 'object' && body !== null && 'menuVersion' in body;
}

/** The checkout body without its menu version: the body the record holds. */
function withoutMenuVersion(body: unknown): unknown {
	const without = { ...(body as Record<string, unknown>) };
	delete without.menuVersion;
	return without;
}

/**
 * The store placed the order the cart sent (#348). A Send places it open: the
 * cart's lines are on it now, and Sell shows it. A sale an older bundle sent
 * with its payment, sent again from its record, comes back paid, and the cart
 * is done.
 */
function sentOrPaid(order: components['schemas']['OrderBody']): void {
	if (order.status === 'open') {
		cartSent(order.id);
		// The answer is the store's own, so it carries the bill's mark and copies.
		openOrder = order as Order;
		return;
	}
	clearDraft();
}

/**
 * A key that placed an order under another body. A Send's names the open
 * order it placed (#348): Sell shows that order as the store holds it. The
 * body differed only by what a check of the key leaves out, the discount or
 * the table, since the cart holds still while its Send waits, so the cart's
 * lines are the ones on it. A sale's, sent with its payment by an older
 * bundle, is the sale that exists, and the cart is done.
 */
async function takenBefore(body: unknown, failure: Problem): Promise<CheckoutOutcome> {
	const orderId = failure.details?.order_id ?? '';
	const paid = typeof body === 'object' && body !== null && 'payment' in body;
	if (paid || !orderId) {
		clearDraft();
		return { kind: 'alreadyPlaced', number: failure.details?.number ?? 0 };
	}
	cartSent(orderId);
	await loadOpenOrder();
	return { kind: 'alreadySent' };
}

/** Whether a checkout body gives the order a discount (UC-35). */
function carriesDiscount(body: unknown): boolean {
	return typeof body === 'object' && body !== null && 'discount' in body;
}

/** Whether a checkout body seats the order at a table (UC-39). */
function carriesTable(body: unknown): boolean {
	return typeof body === 'object' && body !== null && 'tableId' in body;
}

/**
 * Ask whether this key placed the sale, after a refusal of its discount as a
 * whole, or a resend's refusal of a part of it (UC-35, INT-7).
 *
 * An API older than discounts, which a revert put back, refuses a body that
 * names a discount before it reads the key, so its refusal says nothing about
 * a send of this key that the API before the revert placed and whose answer
 * was lost. Its words cannot tell it from the current API's refusal, so every
 * such refusal comes here (refusesTheDiscount). So does a resend's refusal of
 * a part of the discount, which the schema can give before it reads the key
 * (keyAnswered). The same sale without the
 * discount, under the same key, is answered by the key first. A key that
 * placed the sale answers 409 with its number, since the request it stored
 * named the discount. A key that placed none is refused by a rule,
 * TOTAL_MISMATCH above all, because the total the till expects counts the
 * discount, or by a 404 that names an item or an area the use case does not
 * know (keyAnswered); nothing is written, and the key stays free. When the discount took
 * nothing off, no rule refuses the resend, and it places the sale at the same
 * total without the discount's copy. Only those answers end the pending
 * record. Any other leaves the sale's fate unknown, so the record stays and
 * Retry asks again. Once the key is free, the first refusal says what the
 * cashier is told.
 */
async function askTheKey(
	body: unknown,
	key: string,
	first: Problem | null,
	field: 'discount' | 'tableId',
): Promise<CheckoutOutcome> {
	const without = { ...(body as Record<string, unknown>) };
	delete without[field];
	const { data, error, response } = await api.POST('/api/till/orders', {
		body: without as never,
		headers: { 'Idempotency-Key': key },
		querySerializer: askDrivers,
	});
	const fate = checkoutFate(response.status, data, error);
	if (fate === 'placed' && data) {
		endCheckout();
		sentOrPaid(data.order);
		// A table changes no price, so an API a revert put back places the
		// sale without it at the same total. A revert publishes its own
		// bundle too: the till learns of it now and moves to it at rest, never
		// over the Done sheet this sale opens.
		if (field === 'tableId') void checkBuild();
		return { kind: 'placed', placed: data };
	}
	const failure = problemOf(error);
	// The store read nothing, so the sale's fate is still unknown: the record
	// stays, and Retry asks again once the store is ready.
	if (storeNotReady(response.status, failure)) {
		return { kind: 'unconfirmed', code: failure?.code ?? '' };
	}
	if (fate === 'unknown') return unanswered(response.status, failure);
	if (failure?.code === 'IDEMPOTENCY_MISMATCH') {
		endCheckout();
		if (field === 'tableId') void checkBuild();
		return await takenBefore(body, failure);
	}
	if (field === 'tableId' && keyAnswered(response.status, failure)) {
		// No sale holds the key, and a rule of that API refused the sale
		// without its table: the table is off the cart, which that API cannot
		// take, and the cashier is told what the rule said.
		endCheckout();
		setTable(null);
		void loadTables();
		void reloadNowIfNewer();
		if (failure?.code === 'TOTAL_MISMATCH') {
			void loadMenu();
			return { kind: 'priceChanged', totalMinor: failure.details?.totalMinor ?? 0 };
		}
		return { kind: 'refused', code: failure?.code ?? String(response.status) };
	}
	if (keyAnswered(response.status, failure)) {
		// No sale holds the key, and the draft keeps it with the discount.
		endCheckout();
		// The current API refused a cashier's typed discount after it read
		// the key; the cart already says only a manager can give it.
		if (first?.code === 'FORBIDDEN') return { kind: 'discountManagerOnly' };
		// A revert publishes its own bundle too, so the till moves to
		// whatever build the origin serves now, as for a total the rules
		// moved. Until then the cashier takes the discount off to sell. An
		// older API in its schema's words says so plainly; any other words
		// say the discount can't be given, which holds as well.
		void reloadNowIfNewer();
		return discountUnknown(first) ? { kind: 'discountsDown' } : { kind: 'discountRefused' };
	}
	// A refusal that can come before the key is read, such as a session's.
	return { kind: 'unconfirmed', code: failure?.code ?? String(response.status) };
}

/**
 * What the cashier is told when nobody has said what became of the sale: the
 * swap gap of a deploy, a server that failed after it committed, a locked
 * session. The record stays, and Retry sends the same key.
 */
function unanswered(status: number, failure: Problem | null): CheckoutOutcome {
	if (status === 423) return { kind: 'refused', code: failure?.code ?? 'SESSION_LOCKED' };
	return { kind: 'unreachable' };
}

export type AddOutcome =
	| { kind: 'added' }
	// The order closed meanwhile, or another refusal in the store's words.
	| { kind: 'refused'; code: string }
	// The store is not ready to sell yet (#371); nothing was added.
	| { kind: 'notReady' }
	// No answer, or one that says nothing about the key: the lines stay, the
	// cart holds still, and Retry sends the same key.
	| { kind: 'unconfirmed' }
	| { kind: 'unreachable' };

/** A later Send is waiting for an answer that says what became of it. */
export function additionPending(): boolean {
	return currentDraft().additionUnsure;
}

/**
 * A later Send (UC-43, #348): the cart's lines not sent yet go on the open
 * order as one addition, under the draft's key, so a Send repeated by a
 * reload or a Retry writes once.
 *
 * The cart notes the Send before the request, so a till that reloads before
 * the answer still holds the cart still: a Send whose answer was lost may have
 * added the lines, and only an answer about the key says whether. The store
 * reads the key before any rule of the order (UC-43), so an order it says is
 * paid or voided took no line under it.
 */
export async function addToOrder(): Promise<AddOutcome> {
	const draft = currentDraft();
	const orderId = draft.openOrderId;
	const lines = draftBody().lines as never;
	const resend = draft.additionUnsure;
	noteAddition(true);
	try {
		const { data, error, response } = await store.POST('/store/orders/{orderId}/lines', {
			params: { path: { orderId }, header: { 'Idempotency-Key': draft.idempotencyKey } },
			body: { lines },
		});
		online = true;
		if (data) {
			cartSent(orderId);
			openOrder = data.order;
			return { kind: 'added' };
		}
		const failure = problemOf(error);
		if (storeNotReady(response.status, failure)) {
			// The store read nothing: a first Send is simply not made yet.
			if (resend) return { kind: 'unconfirmed' };
			noteAddition(false);
			return { kind: 'notReady' };
		}
		if (response.status < 400 || response.status >= 500 || response.status === 423 || !failure) {
			return { kind: 'unconfirmed' };
		}
		const closed = failure.code === 'ALREADY_PAID' || failure.code === 'ALREADY_VOID';
		if (resend && !closed && !keyAnswered(response.status, failure)) return { kind: 'unconfirmed' };
		noteAddition(false);
		if (closed) {
			// Paid or voided meanwhile: the cart leaves the order, and its
			// lines wait as a new one.
			await loadOpenOrder();
		} else if (failure.code === 'IDEMPOTENCY_MISMATCH') {
			// The key added other lines: the order shows what it holds, and the
			// cart's lines wait under a new key, to be checked before they are
			// sent.
			renewKey();
			await loadOpenOrder();
		}
		return { kind: 'refused', code: failure.code };
	} catch {
		online = false;
		return { kind: 'unreachable' };
	}
}

export type DiscountOutcome =
	{ kind: 'given' } | { kind: 'refused'; code: string } | { kind: 'unreachable' };

/**
 * Give the open order its discount, or take it off with null (UC-35, #348):
 * at once, with no Send. A tap that got no answer is tapped again, and gives
 * the same discount again. Meanwhile Sell shows the order as the store holds
 * it, since the store may have made the change before its answer was lost.
 */
export async function changeOrderDiscount(
	discount: OrderDiscount | null,
): Promise<DiscountOutcome> {
	const orderId = currentDraft().openOrderId;
	const given = discount
		? discount.discountId
			? { discountId: discount.discountId }
			: { kind: discount.kind, value: discount.value }
		: undefined;
	try {
		const { data, error, response } = await store.POST('/store/orders/{orderId}/discount', {
			params: { path: { orderId }, header: { 'Idempotency-Key': newWriteKey() } },
			body: given ? { discount: given } : {},
		});
		online = true;
		if (data) {
			openOrder = data.order;
			return { kind: 'given' };
		}
		const failure = problemOf(error);
		if (
			!failure ||
			response.status >= 500 ||
			failure.code === 'ALREADY_PAID' ||
			failure.code === 'ALREADY_VOID'
		) {
			void loadOpenOrder();
		}
		return { kind: 'refused', code: failure?.code ?? String(response.status) };
	} catch {
		online = false;
		void loadOpenOrder();
		return { kind: 'unreachable' };
	}
}

/** A key for one write of the store's that is tapped again rather than retried. */
export function newWriteKey(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}
}

export type Day = storeComponents['schemas']['DayBody'];

// The trading day the store's day last named, so an open order of an earlier
// day says which (#348). Empty until the day is read.
let tradingDay = $state('');

/** The trading day the till's store last named, or '' before it has. */
export function currentTradingDay(): string {
	return tradingDay;
}

/** Today's orders of this till, from its order store, and the line above them. */
export async function loadDay(): Promise<Day | null> {
	try {
		// Weighed lines whole, with their grams (#917).
		const { data, error } = await api.GET('/api/till/orders', { querySerializer: askWeighed });
		if (error !== undefined || !data) return null;
		online = true;
		tradingDay = data.summary.tradingDay;
		setOpenCount(data.summary.openCount);
		return data as Day;
	} catch {
		online = false;
		return null;
	}
}

/** How often Orders asks the store which sales have not reached Bosla yet. */
export const SENDS_POLL_MS = 4000;

/**
 * This till's closed orders the server has not confirmed (#371, E16): those
 * the store still sends, by id, and those the server refused, by id with its
 * code. Only the store sends, so the screen offers no Retry for either.
 */
export interface Sends {
	/** Order ids, oldest first. */
	waiting: readonly string[];
	/** The server's code, by order id. */
	refused: Readonly<Record<string, string>>;
}

/** What the till's order store says of its unconfirmed sales; null when it cannot say. */
export async function loadSends(): Promise<Sends | null> {
	try {
		const { data, error } = await store.GET('/store/state');
		if (error !== undefined || !data) return null;
		// The same read gives the top bar its count (#352).
		setOpenCount(data.openOrders);
		return {
			waiting: data.waitingOrders,
			refused: Object.fromEntries(data.refused.map((r) => [r.orderId, r.code])),
		};
	} catch {
		return null;
	}
}

export type Settled =
	| { kind: 'settled'; result: Placed }
	// approval: the store wants a manager's approval for this payment
	// (UC-16.R9), which Manager approval (T20) brings.
	| { kind: 'refused'; code: string; approval?: boolean }
	| { kind: 'unreachable' };

/** Take payment on an order the driver has returned from. */
export async function payOrder(
	orderId: string,
	payment: { method: 'cash' | 'card'; tenderedMinor: number },
	key: string,
): Promise<Settled> {
	return settle('/api/till/orders/{orderId}/pay', orderId, { ...payment } as never, key);
}

// A manager's approval (#379): only a manager voids an order or cancels a
// sent line. A manager signed in sends the operation with no approval; anyone
// else sends it with the manager picked on the approval sheet and the
// passcode typed for them, which the store has the server check.

/** The four void reasons, by the key the store takes. */
export type ReasonKey = 'wrong_order' | 'customer_left' | 'rung_up_twice' | 'food_returned';

/** The void reasons as the screen offers them, each with its words. */
export const voidReasons: readonly { key: ReasonKey; words: MessageKey }[] = [
	{ key: 'wrong_order', words: 'ordersVoidWrong' },
	{ key: 'customer_left', words: 'ordersVoidCustomer' },
	{ key: 'rung_up_twice', words: 'ordersVoidMistake' },
	{ key: 'food_returned', words: 'ordersVoidQuality' },
];

/**
 * A void of an order, a cancellation of part or all of one sent line, or a
 * delivery's change of driver (UC-66).
 */
export type ApprovedOperation =
	| { kind: 'void'; orderId: string; reasonKey: ReasonKey; reason: string }
	| { kind: 'driver'; orderId: string; driverId: string }
	// A card payment of a sent delivery that names a driver (UC-16.R9).
	| { kind: 'pay'; orderId: string }
	| {
			kind: 'cancel';
			orderId: string;
			lineId: string;
			qty: number;
			reasonKey: ReasonKey;
			reason: string;
	  };

/** The manager picked on the approval sheet, and the passcode typed for them. */
export interface Approval {
	userId: string;
	passcode: string;
}

export type ApprovedOutcome =
	| { kind: 'done'; order: Order; placed?: Placed }
	| { kind: 'refused'; status: number; code: string; details: Record<string, unknown> | undefined }
	| { kind: 'unreachable' };

/**
 * Send a void or a line cancellation to the till's order store, with the
 * sheet's approval or none. The key names the operation: the same one sent
 * again after a lost answer is answered as before, with no passcode asked
 * again. The order Sell shows follows the answer: what remains of its lines,
 * or, once void, the lines not sent yet stay as a new cart.
 */
export async function runApproved(
	op: ApprovedOperation,
	key: string,
	approval: Approval | null,
): Promise<ApprovedOutcome> {
	if (op.kind === 'driver') return changeDriver(op.orderId, op.driverId, key, approval);
	if (op.kind === 'pay') return payByCardApproved(op.orderId, key, approval);
	const given = approval ? { approval } : {};
	try {
		const { data, error, response } =
			op.kind === 'cancel'
				? await store.POST('/store/orders/{orderId}/lines/{lineId}/cancel', {
						params: {
							path: { orderId: op.orderId, lineId: op.lineId },
							header: { 'Idempotency-Key': key },
						},
						body: { qty: op.qty, reasonKey: op.reasonKey, reason: op.reason, ...given },
					})
				: await store.POST('/store/orders/{orderId}/void', {
						params: { path: { orderId: op.orderId }, header: { 'Idempotency-Key': key } },
						body: { reasonKey: op.reasonKey, reason: op.reason, ...given },
					});
		online = true;
		if (data) {
			followOrder(data.order);
			return { kind: 'done', order: data.order };
		}
		const failure = problemOf(error);
		if (failure?.code === 'ALREADY_PAID' || failure?.code === 'ALREADY_VOID') void loadOpenOrder();
		return {
			kind: 'refused',
			status: response.status,
			code: failure?.code ?? '',
			details: failure?.details,
		};
	} catch {
		online = false;
		return { kind: 'unreachable' };
	}
}

/**
 * A card payment of a sent delivery that names a driver (UC-16.R9), with the
 * approval sheet's manager or none when a manager is signed in. The key is
 * the payment's own, so a payment whose answer was lost is answered as before,
 * with no passcode asked again.
 */
export async function payByCardApproved(
	orderId: string,
	key: string,
	approval: Approval | null,
): Promise<ApprovedOutcome> {
	try {
		const { data, error, response } = await store.POST('/api/till/orders/{orderId}/pay', {
			params: { path: { orderId } },
			body: { method: 'card', tenderedMinor: 0, ...(approval ? { approval } : {}) },
			headers: { 'Idempotency-Key': key },
		});
		online = true;
		if (data) return { kind: 'done', order: data.order, placed: data };
		const failure = problemOf(error);
		if (failure?.code === 'ALREADY_PAID' || failure?.code === 'ALREADY_VOID') void loadOpenOrder();
		return {
			kind: 'refused',
			status: response.status,
			code: failure?.code ?? '',
			details: failure?.details,
		};
	} catch {
		online = false;
		return { kind: 'unreachable' };
	}
}

/**
 * Whether a payment by card of this order needs a manager (UC-16.R9): a sent
 * delivery that names a driver. The rule is the store's; the till asks for the
 * approval up front so a cashier is not refused first.
 */
export function cardNeedsManager(order: { type: string; driverId?: string | null }): boolean {
	return order.type === 'delivery' && !!order.driverId;
}

/**
 * A delivery's driver changed (UC-66, T33 in change mode): the open order of
 * this till goes with another of its shop's active drivers, with a manager's
 * approval or none when a manager is signed in. The key names the change, so
 * the same one sent again after a lost answer is answered as before, with no
 * passcode asked again.
 */
export async function changeDriver(
	orderId: string,
	driverId: string,
	key: string,
	approval: Approval | null,
): Promise<ApprovedOutcome> {
	try {
		const { data, error, response } = await store.POST('/store/orders/{orderId}/driver', {
			params: { path: { orderId }, header: { 'Idempotency-Key': key } },
			body: { driverId, ...(approval ? { approval } : {}) },
		});
		online = true;
		if (data) {
			followOrder(data.order);
			return { kind: 'done', order: data.order };
		}
		const failure = problemOf(error);
		if (failure?.code === 'ALREADY_PAID' || failure?.code === 'ALREADY_VOID') void loadOpenOrder();
		// A driver removed meanwhile, or one the store holds no list naming:
		// the list is read again, so the sheet offers only those who are left.
		if (
			failure?.code === 'DRIVER_INACTIVE' ||
			(failure?.code === 'NOT_FOUND' && failure.details?.path === 'driverId')
		) {
			void loadDrivers();
		}
		return {
			kind: 'refused',
			status: response.status,
			code: failure?.code ?? '',
			details: failure?.details,
		};
	} catch {
		online = false;
		return { kind: 'unreachable' };
	}
}

/** What an approval's answer means to the sheet's passcode entry. */
export function approvalResult(outcome: ApprovedOutcome): PasscodeResult<Order> {
	switch (outcome.kind) {
		case 'done':
			return { ok: true, value: outcome.order };
		case 'unreachable':
			return { ok: false, failure: 'unreachable' };
	}
	if (outcome.status === 423 && outcome.code === 'PASSCODE_LOCKED') {
		return {
			ok: false,
			failure: 'locked',
			lockSeconds: retryAfterSeconds(outcome.details),
			wholeRestaurant: outcome.details?.scope === 'account',
		};
	}
	if (outcome.code === 'PASSCODE_WRONG') return { ok: false, failure: 'wrong' };
	if (outcome.code === 'FORBIDDEN') return { ok: false, failure: 'forbidden' };
	// The store's empty 503: it had no answer from the server to check the
	// passcode with. One that is not ready to sell yet has none either.
	if (outcome.status === 502 || outcome.status === 503 || outcome.status === 504) {
		return { ok: false, failure: 'internet' };
	}
	return { ok: false, failure: 'refused' };
}

/** Sell follows an order a void or a cancellation answered with. */
function followOrder(order: Order): void {
	if (currentDraft().openOrderId !== order.id) return;
	if (order.status === 'open') {
		openOrder = order;
		return;
	}
	// The last sent item was cancelled, so the order is void: the lines not
	// sent yet stay in the cart as a new order.
	openOrder = null;
	if (currentDraft().lines.length === 0) clearDraft();
	else forgetOpenOrder();
}

async function settle(
	path: '/api/till/orders/{orderId}/pay',
	orderId: string,
	body: never,
	key: string,
): Promise<Settled> {
	try {
		const { data, error, response } = await api.POST(path, {
			params: { path: { orderId } },
			body,
			headers: { 'Idempotency-Key': key },
		});
		online = true;
		if (error === undefined && data) return { kind: 'settled', result: data };
		const failure = problemOf(error);
		return {
			kind: 'refused',
			code: failure?.code ?? String(response.status),
			approval: failure?.code === 'FORBIDDEN' && failure.details?.approval === 'required',
		};
	} catch {
		online = false;
		return { kind: 'unreachable' };
	}
}
